import React from 'react';
import styled from 'styled-components';

import { ListingLabelContext } from 'contexts';
import ListingSummaryProps from '../../types/listingSummaryPropsType';
import {
  theme,
  Tooltip,
  Icon,
  Text,
  Paragraph,
  Avatar,
  ListingImage,
} from '../../components';
import { formatCurrency, getProvinceAbbreviation } from '../../utils';
import { timeFormatter } from '../../Router/pages/TourEditPage/utils';

// todo: this component is partially stolen from cross-org, maybe can refactor after merged in?
const ListingSummary: React.FC<ListingSummaryProps> = ({
  thumbnail,
  line1,
  line2,
  province,
  city,
  availability,
  list_price,
  status,
  mls_numbers,
  dom,
  isShowingRequestItem,
  org,
  time,
  timeEnd,
  originalTime,
  originalTimeEnd,
  timezone,
  address,
  locationName,
}) => {
  const { getLabel, getAvailability, getBoardName } =
    React.useContext(ListingLabelContext);

  return (
    <StyledListingSummary $isShowingRequestItem={isShowingRequestItem}>
      <StyledPreviewContainer>
        <ListingImage imageSrc={thumbnail} size={64} fixProportion />
      </StyledPreviewContainer>
      <StyledAddressAndTimeContainer>
        <NoWrapRow>
          <StyledAvailability availability={availability}>
            {getAvailability(availability)}
          </StyledAvailability>
          <Text type="secondary">&nbsp;•&nbsp;</Text>
          <Text
            type="secondary"
            ellipsis={mls_numbers?.length > 1 ? 190 : false}
            data-cy="board-numbers-or-listingStatus"
          >
            {status === 'MLS' && mls_numbers?.length
              ? mls_numbers?.map(({ board, number }, i) => (
                  <span key={`${number}${i}`}>
                    {getBoardName(board)}: {number}
                    {i < mls_numbers.length - 1 && ', '}
                  </span>
                ))
              : getLabel(status)}
          </Text>
        </NoWrapRow>
        <WrapRow>
          {isShowingRequestItem ? (
            <NoWrapText $bold ellipsis>
              {locationName ?? address}
            </NoWrapText>
          ) : (
            <>
              {' '}
              <span>
                <NoWrapText
                  $bold
                  ellipsis={line1.trim().length > 20 ? 140 : false}
                >
                  {line1.trim()}
                </NoWrapText>
                {line2 && <NoWrapText $bold>{`, ${line2}`}</NoWrapText>}
                &nbsp;
              </span>
              <span>
                <NoWrapText type="secondary">{city}</NoWrapText>
                <NoWrapText type="secondary">
                  ,&nbsp;{getProvinceAbbreviation(province)}
                </NoWrapText>
              </span>
            </>
          )}
        </WrapRow>
        {!isShowingRequestItem && (
          <NoWrapRow>
            <Text>{formatCurrency(list_price)}&nbsp;</Text>
            <Tooltip title={`${dom} ${dom === 1 ? 'Day' : 'Days'} on Market`}>
              <Text type="secondary">
                <NoWrapRow style={{ marginLeft: '6px' }}>
                  <Icon
                    type="antdClockCircle"
                    width={9}
                    height={9}
                    style={{ marginRight: '6px' }}
                  />
                  {dom}d
                </NoWrapRow>
              </Text>
            </Tooltip>
          </NoWrapRow>
        )}
        {isShowingRequestItem && (
          <StyledTimeText>
            {status === 'time_change' && originalTime && originalTimeEnd
              ? `${timeFormatter(originalTime, timezone)} - ${timeFormatter(
                  originalTimeEnd,
                  timezone,
                )}`
              : `${timeFormatter(time, timezone)} - ${timeFormatter(
                  timeEnd,
                  timezone,
                )}`}
          </StyledTimeText>
        )}
      </StyledAddressAndTimeContainer>
      {isShowingRequestItem && org && (
        <>
          <StyledAvatarContainer>
            <Avatar
              width={30}
              src={org.logoUrl}
              user={{ firstName: org.name }}
            />
          </StyledAvatarContainer>
          <StyledParagrapgh
            ellipsis={{ rows: 4 }}
            style={{ marginBottom: '0px' }}
          >
            {org.name}
          </StyledParagrapgh>
        </>
      )}
    </StyledListingSummary>
  );
};

export default ListingSummary;

const StyledPreviewContainer = styled.div`
  align-items: flex-start;
  width: 64px;
  height: 64px;
  left: 0px;
  top: 14px;
  object-fit: cover;
  border-radius: 6.1px;
  overflow: hidden;
`;

const StyledListingSummary = styled.div<{ $isShowingRequestItem: boolean }>`
  margin-top: 14px;
  display: flex;
  ${({ $isShowingRequestItem }) =>
    $isShowingRequestItem &&
    `margin-top: 2.4rem;
     border: 1px solid #dddddd;
     border-radius: 4px;
     padding: 1.3rem;
     align-items: center;`}
`;

const NoWrapRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  margin: 1px 0;
  span {
    font-size: 90%;
  }
`;

const WrapRow = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const NoWrapText = styled(Text)<{ $bold?: boolean }>`
  white-space: nowrap;
  ${({ $bold }) => $bold && 'font-weight: 500;'}
`;

const StyledAvailability = styled(Text)<{ availability: string }>`
  white-space: nowrap;
  color: ${({ availability }) => {
    switch (availability) {
      case 'New':
      case 'Extended':
      case 'Price Change':
        return theme.blue6;
      case 'Sold':
      case 'Leased':
        return theme.green6;
      case 'Sold Conditionally':
      case 'Leased Conditionally':
        return theme.warningColor;
      default:
        return theme.textColorSecondary;
    }
  }};
`;

const StyledAddressAndTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 1.7rem;
  flex-grow: 1;
`;

const StyledAvatarContainer = styled.div`
  margin: 0 1rem;
`;

const StyledParagrapgh = styled(Paragraph)`
  width: 17.5rem;
`;

const StyledTimeText = styled(Text)`
  color: rgba(0, 0, 0, 0.65);
`;
